import AddIcon from "@mui/icons-material/Add";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FastForwardIcon from "@mui/icons-material/FastForward";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  Box,
  Button,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  submitArchiveMeeting,
  updateInvitation,
  requestedReschedule,
  updateAvailableTimeSlots,
} from "../../../../apis/invitations.api";
import { AppModalDialogBox } from "../../../../components/modals";
import { AppAlertBox } from "../../../../components/modals/alert-box";
import { ExpandableMemberAvatarGroup } from "../../../../components/member-avatar-group";
import {
  CONST_PROJECT_STATUS_LIST,
} from "../../../../constants/lists";
import {
  ENUM_PROJECT_STATUS,
} from "../../../../enums";
import useToggle from "../../../../hooks/useToggle";
import { convertToFullName } from "../../../../services/utils";
import { RescheduleInvitationModal } from "../../../invitations/list/reschedule-invitation";
import clientHOC from "../../client-hoc";
import AmbassadorAnswersModal from "./ambassador-answers";
import AmbassadorSuggestedSlotsModal from "./ambassador-suggested-slots-modal";
import { AmbassadorWiseSlotDetailsListTable } from "./ambassador-wise-slot-details-table";
import ScheduleManualInvitationModal from "./ScheduleManualInvitationModal";
import { AMBASSADOR_INVITATION_STATUS_LIST_COLUMNS } from "./table-config";
import TransferRoleModal from "./transfer-role-modal";
import VendorSuggestedSlotsModal from "./vendor-suggested-slots-modal";
import { RescheduleSlotsModal } from "./reschedule-slots-modal";
import { EditVendorSlotsModal } from "./edit-vendor-slots-modal";
import { ProjectStatusTimeline } from "../../../invitations/components/project-status-timeline";
import { canShowSendMeetingReminder } from "../../../invitations/components/project-status-timeline/utils";
import { InvitationShareLink } from "./invitation-share-link";
import { AddMemberModal } from "../../../dashboard/meeting-calendar/add-member-modal";
import {
  DATE_FORMATS,
  formatDate,
} from "../../../../services/date-and-time.utils";
import {
  ActionCard,
  CompactListItemButton,
  ModernAccordion,
  ModernAccordionDetails,
  ModernAccordionSummary,
  SectionTitle,
} from "./styles";

function canShowDelegateTransfer(invitationStatus) {
  const statusConfig = CONST_PROJECT_STATUS_LIST[invitationStatus];
  if (!statusConfig?.step || statusConfig.isClosedStatus) return false;

  const approvedStep =
    CONST_PROJECT_STATUS_LIST[ENUM_PROJECT_STATUS.APPROVED_BY_CLIENT].step;
  const scheduledStep =
    CONST_PROJECT_STATUS_LIST[ENUM_PROJECT_STATUS.SEND_MEETING_LINK].step;

  return (
    statusConfig.step >= approvedStep && statusConfig.step < scheduledStep
  );
}

function ClientInvitationStatusListExpandedRowData({
  data,
  clientDetails,
  getClientInvitationStatusList,
}: any) {
  const { selectedPage, selectedPageSize, selectedDate } = useSelector(
    (state: any) => state.clients.info.invitationStatus.filters
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const invitationId = searchParams.get("invitationId");

  const accordionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [expandedAmbassadors, setExpandedAmbassadors] = useState<
    Record<string, boolean>
  >({});

  const handleArchieveSuccess = () => {
    getClientInvitationStatusList({
      selectedPage,
      selectedPageSize,
      selectedDate: selectedDate || "",
    });
  };

  const handleScheduleInvitationsModalSubmit = () => {
    getClientInvitationStatusList({
      selectedPage,
      selectedPageSize,
      selectedDate: selectedDate || "",
    });
  };

  const handleDelegateTransferSuccess = () => {
    getClientInvitationStatusList({
      selectedPage,
      selectedPageSize,
      selectedDate: selectedDate || "",
    });
  };

  useEffect(() => {
    if (!invitationId || !data?.ambassadors?.length) return;

    setExpandedAmbassadors((prev) => {
      let hasUpdates = false;
      const next = { ...prev };

      data.ambassadors.forEach((ambassador) => {
        const ambId = ambassador?.ambassadorInvitationId;
        const isTargetInvitation = ambassador?.history?.some(
          (historyItem: any) => historyItem?._id === invitationId
        );

        if (ambId && isTargetInvitation && !next[ambId]) {
          next[ambId] = true;
          hasUpdates = true;
        }
      });

      return hasUpdates ? next : prev;
    });
  }, [invitationId, data?.ambassadors]);

  useEffect(() => {
    if (invitationId && accordionRefs.current[invitationId]) {
      setTimeout(() => {
        accordionRefs.current[invitationId]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        highlightTableRow(accordionRefs.current[invitationId], invitationId);
      }, 500);
    }
  }, [invitationId, data]);

  return (
    <Grid container>
      <Grid item xs={12} px={{ xs: 0, md: 2 }}>
        {data?.ambassadors?.map((ambassador, slotListIndex) => {
          const ambassadorInvitationStatus = ambassador?.invitationStatus;
          const statusLabel =
            CONST_PROJECT_STATUS_LIST[ambassadorInvitationStatus]?.label ||
            "Unknown";

          // Check if any history item matches the invitationId (only if invitationId exists)
          const isTargetInvitation =
            invitationId &&
            ambassador?.history?.some(
              (historyItem: any) => historyItem?._id === invitationId
            );
          const ambassadorInvitationId = ambassador?.ambassadorInvitationId;
          const showSendReminder = canShowSendMeetingReminder(ambassador);
          const isAccordionExpanded = Boolean(
            ambassadorInvitationId && expandedAmbassadors[ambassadorInvitationId]
          );

          return (
            <ModernAccordion
              key={`accordion-${slotListIndex}`}
              expanded={isAccordionExpanded}
              onChange={(_, expanded) => {
                if (!ambassadorInvitationId) return;
                setExpandedAmbassadors((prev) => ({
                  ...prev,
                  [ambassadorInvitationId]: expanded,
                }));
              }}
              ref={(el) => {
                if (isTargetInvitation && invitationId) {
                  accordionRefs.current[invitationId] = el;
                }
              }}
            >
              <ModernAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                sx={{
                  // Use the exact color from constants without modifying alpha so named/8-digit hex colors remain valid
                  background: `linear-gradient(135deg, ${getAccordionBgColor(
                    ambassador
                  )} 0%, ${getAccordionBgColor(ambassador)} 100%)`,
                  color: "white",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, fontSize: "16px" }}
                    >
                      {convertToFullName(
                        ambassador?.firstName,
                        ambassador?.lastName
                      )}
                    </Typography>
                    {data.isDummy && (
                      <Chip
                        icon={<FastForwardIcon sx={{ fontSize: "16px" }} />}
                        label="Manual meeting"
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.2)",
                          color: "white",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          "& .MuiChip-icon": {
                            color: "white",
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={statusLabel}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        color: "#1F2937",
                        border: "1px solid rgba(255, 255, 255, 0.8)",
                        height: "24px",
                        fontSize: "10px",
                        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
                        "& .MuiChip-label": {
                          fontSize: "10px",
                          padding: "0 8px",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </ModernAccordionSummary>
              <ModernAccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={9}>
                    <Box mt={1}>
                      <AmbassadorWiseSlotDetailsListTable
                        key={`slot-details-${ambassador?.id}-${slotListIndex}`}
                        rows={ambassador?.history}
                        columns={AMBASSADOR_INVITATION_STATUS_LIST_COLUMNS}
                        archiveSecondaryStatus={ambassador?.secondaryStatus}
                        rejectMessage={ambassador?.rejectMessage}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <ActionCard sx={{ mt: 1 }}>
                      <CardContent sx={{ p: 0 }}>
                        <Box
                          sx={{
                            p: 1,
                            backgroundColor: "#F8FAFC",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#374151",
                              margin: 0,
                            }}
                          >
                            Actions
                          </Typography>
                        </Box>
                        <Box sx={{ p: 1 }}>
                          <SectionTitle>Scheduling</SectionTitle>
                          <Stack spacing={0}>
                            <SheduleInvitationModalBtn
                              invitation={ambassador}
                              clientDetails={clientDetails}
                              onSubmit={handleScheduleInvitationsModalSubmit}
                            />
                            {ambassadorInvitationStatus ===
                              ENUM_PROJECT_STATUS.SEND_MEETING_LINK && (
                              <RescheduleSlotsButton
                                invitation={ambassador}
                                clientDetails={clientDetails}
                                onSubmit={handleScheduleInvitationsModalSubmit}
                              />
                            )}
                            {ambassador?.availableTimeSlots?.length > 0 && (
                              <ShowSlotsModalButton
                                data={ambassador}
                                clientDetails={clientDetails}
                                onSubmit={handleScheduleInvitationsModalSubmit}
                              />
                            )}
                            {ambassador?.suggestedTimeSlots?.length > 0 && (
                              <ShowAmbassadorSuggestedSlotsModalButton
                                data={ambassador}
                              />
                            )}
                            {CONST_PROJECT_STATUS_LIST[
                              ambassadorInvitationStatus
                            ]?.step >= 3 &&
                              ambassador?.availableTimeSlots?.length < 1 && (
                                <Typography
                                  fontSize={12}
                                  color={"primary"}
                                  sx={{
                                    mt: 0.5,
                                    p: 1,
                                    backgroundColor: "#EFF6FF",
                                    border: "1px solid #DBEAFE",
                                  }}
                                >
                                  Vendor has Calendly integration enabled
                                </Typography>
                              )}
                          </Stack>
                          <InvitationShareLink
                            ambassadorInvitationId={ambassadorInvitationId}
                            refreshKey={ambassadorInvitationStatus}
                            enabled={isAccordionExpanded}
                          />
                          {(() => {
                            const canDelegate = canShowDelegateTransfer(
                              ambassadorInvitationStatus
                            );
                            const delegateData = ambassador?.delegateData || [];

                            if (!canDelegate && !delegateData.length) return null;

                            return (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <SectionTitle>Delegation</SectionTitle>
                                <DelegateMemberAvatarGroup
                                  members={delegateData}
                                  showAddButton={canDelegate}
                                  ambassadorInvitationId={
                                    ambassador?.ambassadorInvitationId
                                  }
                                  onTransferSuccess={handleDelegateTransferSuccess}
                                />
                              </>
                            );
                          })()}
                          {(() => {
                            const additionalMembers =
                              ambassador?.additionalMembers ||
                              ambassador?.history?.[
                                ambassador?.history?.length - 1
                              ]?.additionalMembers ||
                              [];
                            const canEditAttendees =
                              ambassadorInvitationStatus !==
                              ENUM_PROJECT_STATUS.ARCHIVE;

                            if (!canEditAttendees && !additionalMembers.length) {
                              return null;
                            }

                            return (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <SectionTitle>Other Attendees</SectionTitle>
                                <AdditionalAttendeesMemberAvatarGroup
                                  members={additionalMembers}
                                  showAddButton={canEditAttendees}
                                  ambassador={ambassador}
                                  clientDetails={clientDetails}
                                  onSuccess={handleScheduleInvitationsModalSubmit}
                                />
                              </>
                            );
                          })()}
                          {ambassador?.questions && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <SectionTitle>Notes & Answers</SectionTitle>
                                <Stack spacing={0}>
                                  <ShowAnswersModalButton
                                    questions={ambassador?.questions}
                                    clientDetails={clientDetails}
                                  />
                                </Stack>
                              </>
                            )}
                          {ambassadorInvitationStatus !==
                            ENUM_PROJECT_STATUS.ARCHIVE && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <SectionTitle>Administration</SectionTitle>
                                <Stack spacing={0}>
                                  <InvitationArchieveButton
                                    data={ambassador}
                                    onArchieveSuccess={handleArchieveSuccess}
                                    clientDetails={clientDetails}
                                  />
                                </Stack>
                              </>
                            )}
                          <Divider sx={{ my: 1 }} />
                          <ProjectStatusTimeline
                            status={ambassadorInvitationStatus}
                            selectedTimeSlot={ambassador?.selectedTimeSlot}
                            ambassadorInvitationId={ambassadorInvitationId}
                            showSendReminder={showSendReminder}
                          />
                        </Box>
                      </CardContent>
                    </ActionCard>
                  </Grid>
                </Grid>
              </ModernAccordionDetails>
            </ModernAccordion>
          );
        })}
      </Grid>
    </Grid>
  );
}

function getAccordionBgColor(ambassador) {
  const status = ambassador?.invitationStatus;

  return CONST_PROJECT_STATUS_LIST[status]?.bgColor || "black";
}

function ShowAnswersModalButton({ questions, clientDetails }) {
  const [showAnswersModal, setShowAnswersModal] = useState(false);

  const handleShowAnswerClick = () => {
    setShowAnswersModal(!showAnswersModal);
  };

  return (
    <>
      <CompactListItemButton onClick={handleShowAnswerClick}>
        <ListItemIcon>
          <QuizOutlinedIcon sx={{ fontSize: "16px" }} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{
            fontSize: 12,
            fontWeight: 500,
            display: "inline-block",
            boxShadow: "inset 0 -1px 0 0 currentColor", // line at bottom
            pb: "1px",
          }}
          primary="View Q&A"
        />
      </CompactListItemButton>
      {showAnswersModal && (
        <AmbassadorAnswersModal
          questions={questions}
          clientDetails={clientDetails}
          onClose={handleShowAnswerClick}
        />
      )}
    </>
  );
}

function ShowAmbassadorSuggestedSlotsModalButton({ data }) {
  const [showSlotaModal, setShowSlotsModal] = useState(false);

  const handleShowSlotsClick = () => {
    setShowSlotsModal(!showSlotaModal);
  };

  return (
    <>
      <CompactListItemButton onClick={handleShowSlotsClick}>
        <ListItemIcon>
          <EventAvailableOutlinedIcon sx={{ fontSize: "16px" }} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{
            fontSize: 12,
            fontWeight: 500,
            display: "inline-block",
            boxShadow: "inset 0 -1px 0 0 currentColor", // line at bottom
            pb: "1px",
          }}
          primary="Ambassador proposed times"
        />
      </CompactListItemButton>
      {showSlotaModal && (
        <AmbassadorSuggestedSlotsModal
          onClose={handleShowSlotsClick}
          data={data}
        />
      )}
    </>
  );
}

function ShowSlotsModalButton({ data, clientDetails, onSubmit }) {
  const dispatch = useDispatch();
  const [showSlotaModal, setShowSlotsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const invitationStatus = data?.invitationStatus;
  const showEditIcon =
    [
      ENUM_PROJECT_STATUS.APPROVED_BY_CLIENT,
      ENUM_PROJECT_STATUS.ADDED_TIME_SLOTS_BY_CLIENT,
      ENUM_PROJECT_STATUS.SUGGESTED_TIME_SLOT_BY_AMBASSADOR,
    ].includes(invitationStatus) && !data?.isCalendly;

  const handleShowSlotsClick = () => {
    setShowSlotsModal(!showSlotaModal);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
  };

  const handleEditSubmit = async (payload) => {
    const invitationId = data?.ambassadorInvitationId;
    if (!invitationId) return;

    const availableTimeSlots = payload.slots.map((slot) => ({
      from: slot.from.toISOString(),
      to: slot.to.toISOString(),
    }));

    const response: any = await dispatch(
      updateAvailableTimeSlots(invitationId, availableTimeSlots)
    );

    if (response?.success) {
      setShowEditModal(false);
      onSubmit?.();
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <CompactListItemButton onClick={handleShowSlotsClick} sx={{ flex: 1 }}>
          <ListItemIcon>
            <CalendarMonthOutlinedIcon sx={{ fontSize: "16px" }} />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{
              fontSize: 12,
              fontWeight: 500,
              display: "inline-block",
              boxShadow: "inset 0 -1px 0 0 currentColor", // line at bottom
              pb: "1px",
            }}
            primary="Vendor proposed times"
          />
        </CompactListItemButton>
        {showEditIcon ? (
          <IconButton
            size="small"
            aria-label="Edit vendor proposed times"
            onClick={() => setShowEditModal(true)}
          >
            <EditOutlinedIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        ) : null}
      </Box>
      {showSlotaModal && (
        <VendorSuggestedSlotsModal onClose={handleShowSlotsClick} data={data} />
      )}
      {showEditModal && (
        <EditVendorSlotsModal
          handleModalClose={handleEditModalClose}
          invitationData={{
            availableTimeSlots: data?.availableTimeSlots,
            ambassador: {
              firstName: data?.firstName,
              lastName: data?.lastName,
              timezone: data?.timezone,
            },
            client: { ...clientDetails },
          }}
          onSubmit={handleEditSubmit}
        />
      )}
    </>
  );
}

function InvitationArchieveButton({ clientDetails, data, onArchieveSuccess }) {
  const dispatch = useDispatch();
  const [showArhieveConfirmModal, setShowArhieveConfirmModal] = useState(false);
  const [archiveReason, setArchiveReason] = useState("cancelled");
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  const handleArchiveMeeting = async () => {
    if (archiveReason === "re_schedule") {
      setShowArhieveConfirmModal(false);
      setShowRescheduleModal(true);
      return;
    }

    const response: any = await dispatch(
      submitArchiveMeeting({
        id: data?.ambassadorInvitationId,
        action: archiveReason,
      })
    );

    if (response?.success) {
      onArchieveSuccess();
    }
  };

  const handleArchiveMeetingClick = () => {
    setShowArhieveConfirmModal(true);
  };

  const handleArchiveReasonChange = (event) => {
    setArchiveReason(event.target.value);
  };

  const handleRescheduleSubmit = async (payload) => {
    const response: any = await dispatch(updateInvitation(payload));

    if (response.success) {
      setShowRescheduleModal(false);
      onArchieveSuccess();
    }
  };

  const showRechedule =
    data?.invitationStatus === ENUM_PROJECT_STATUS.SEND_MEETING_LINK;

  return (
    <>
      <CompactListItemButton onClick={handleArchiveMeetingClick}>
        <ListItemIcon>
          <ArchiveRoundedIcon color="error" sx={{ fontSize: "16px" }} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{
            fontSize: 12,
            fontWeight: 500,
            color: "error.main",
            display: "inline-block",
            boxShadow: "inset 0 -1px 0 0 currentColor", // line at bottom
            pb: "1px",
          }}
          primary="Archive"
        />
      </CompactListItemButton>
      {showArhieveConfirmModal && (
        <AppAlertBox
          isModalOpen={showArhieveConfirmModal}
          onConfirm={handleArchiveMeeting}
          onCancel={() => {
            setShowArhieveConfirmModal(false);
          }}
          message={
            <Box display={"flex"}>
              <FormControl>
                <FormLabel id="archive-radio-buttons-group-label">
                  Choose Reason
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="archive-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={archiveReason}
                  onChange={handleArchiveReasonChange}
                >
                  <FormControlLabel
                    value="cancelled"
                    control={<Radio />}
                    label="Cancelled"
                  />
                  {showRechedule && (
                    <FormControlLabel
                      value="re_schedule"
                      control={<Radio />}
                      label="Rescheduled"
                    />
                  )}
                </RadioGroup>
              </FormControl>
            </Box>
          }
        />
      )}
      {showRescheduleModal && (
        <RescheduleInvitationModal
          handleModalClose={() => setShowRescheduleModal(false)}
          invitationId={data?.ambassadorInvitationId}
          handleSubmit={handleRescheduleSubmit}
          invitationData={{
            ambassador: {
              firstName: data?.firstName,
              lastName: data?.lastName,
              timezone: data?.timezone,
            },
            client: { ...clientDetails },
          }}
          selectedValue={{
            from: data?.selectedTimeSlot?.from || new Date(),
          }}
          additionalMembers={
            data?.additionalMembers ||
            data?.history?.[data?.history?.length - 1]?.additionalMembers ||
            []
          }
        />
      )}
    </>
  );
}

function AdditionalAttendeesMemberAvatarGroup({
  members = [],
  showAddButton,
  ambassador,
  clientDetails,
  onSuccess,
}) {
  const [showAddMemberModal, setShowAddMemberModal] = useToggle(false);

  const handleModalClose = () => {
    setShowAddMemberModal();
  };

  const handleAddClick = () => {
    setShowAddMemberModal();
  };

  const meetingTime = ambassador?.selectedTimeSlot?.from
    ? formatDate(
        ambassador.selectedTimeSlot.from,
        DATE_FORMATS.fullDateWithTime
      )
    : "—";

  return (
    <>
      <ExpandableMemberAvatarGroup
        members={members}
        showAddButton={showAddButton}
        onAddClick={handleAddClick}
        addButtonTitle="Add or edit attendees"
      />
      {showAddMemberModal && (
        <AddMemberModal
          event={{
            ambassador: {
              firstName: ambassador?.firstName,
              lastName: ambassador?.lastName,
            },
            client: clientDetails,
            datetime: meetingTime,
            iId: ambassador?.ambassadorInvitationId,
          }}
          modalTitle={members?.length ? "Edit Attendees" : "Add Attendees"}
          initialMembers={members}
          onClose={handleModalClose}
          onSuccess={onSuccess}
        />
      )}
    </>
  );
}

function DelegateMemberAvatarGroup({
  members,
  showAddButton,
  ambassadorInvitationId,
  onTransferSuccess,
}) {
  const [showTransferModal, setShowTransferModal] = useToggle(false);

  const handleTransferModalClose = () => {
    setShowTransferModal();
  };

  const handleAddClick = () => {
    setShowTransferModal();
  };

  return (
    <>
      <ExpandableMemberAvatarGroup
        members={members}
        showAddButton={showAddButton}
        onAddClick={handleAddClick}
        addButtonTitle="Delegate scheduling to teammate"
      />
      {showTransferModal && (
        <AppModalDialogBox
          title="Delegate Transfer"
          onClose={handleTransferModalClose}
          isModalOpen={showTransferModal}
        >
          <TransferRoleModal
            onClose={handleTransferModalClose}
            onSuccess={onTransferSuccess}
            id={ambassadorInvitationId}
          />
        </AppModalDialogBox>
      )}
    </>
  );
}

function SheduleInvitationModalBtn({ clientDetails, invitation, onSubmit }) {
  const [scheduleInvitationsModal, setScheduleInvitationsModal] =
    useState(false);

  const handleModalClose = () => {
    setScheduleInvitationsModal(false);
  };

  const handleSubmit = () => {
    setScheduleInvitationsModal(false);
    onSubmit();
  };

  return (
    <React.Fragment>
      <Button
        onClick={() => setScheduleInvitationsModal(true)}
        variant="contained"
        color="primary"
        startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
        sx={{
          justifyContent: "flex-start",
          width: "fit-content",
          textTransform: "none",
          fontSize: 12,
          fontWeight: 500,
          marginLeft: "10px",
        }}
        fullWidth={false}
        size="small"
      >
        Schedule
      </Button>
      {scheduleInvitationsModal && (
        <ScheduleManualInvitationModal
          invitation={{
            ...invitation,
            ambassador: {
              firstName: invitation?.firstName,
              lastName: invitation?.lastName,
              timezone: invitation?.timezone,
            },
            client: { ...clientDetails },
          }}
          onSubmit={handleSubmit}
          onClose={handleModalClose}
        />
      )}
    </React.Fragment>
  );
}

function RescheduleSlotsButton({ clientDetails, invitation, onSubmit }) {
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const dispatch = useDispatch();

  const handleModalClose = () => {
    setShowRescheduleModal(false);
  };

  const handleSubmit = async (payload) => {
    const invitationId = invitation?.ambassadorInvitationId;
    if (!invitationId) return;

    const isClient = payload.selectedType === "vendor";
    const requestAvailabilityOnly = Boolean(payload.selfTimeSlotsSelection);

    const apiPayload: any = {
      isClient,
      requestAvailabilityOnly,
    };

    if (!requestAvailabilityOnly) {
      const timeSlots = payload.slots.map((slot) => ({
        from: slot.from.toISOString(),
        to: slot.to.toISOString(),
      }));

      apiPayload[isClient ? "availableTimeSlots" : "suggestedTimeSlots"] =
        timeSlots;
    }

    const response: any = await dispatch(
      requestedReschedule(invitationId, apiPayload)
    );

    if (response?.success) {
      setShowRescheduleModal(false);
      onSubmit();
    }
  };

  return (
    <>
      <CompactListItemButton onClick={() => setShowRescheduleModal(true)}>
        <ListItemIcon>
          <ScheduleIcon sx={{ fontSize: "16px" }} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{
            fontSize: 12,
            fontWeight: 500,
            display: "inline-block",
            boxShadow: "inset 0 -1px 0 0 currentColor",
            pb: "1px",
          }}
          primary="Reschedule"
        />
      </CompactListItemButton>
      {showRescheduleModal && (
        <RescheduleSlotsModal
          handleModalClose={handleModalClose}
          invitationData={{
            ambassador: {
              firstName: invitation?.firstName,
              lastName: invitation?.lastName,
              timezone: invitation?.timezone,
            },
            client: { ...clientDetails },
          }}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}

function highlightTableRow(
  accordionElement: HTMLDivElement | null,
  invitationId: string
) {
  if (!accordionElement || !invitationId) return;

  // Find and highlight the specific table row
  const tableRows = accordionElement.querySelectorAll("tbody tr");
  tableRows?.forEach((row: any) => {
    const rowData = row.getAttribute("data-row-id");
    if (rowData === invitationId) {
      // Add highlight animation
      row.style.animation = "highlight-pulse 1s ease-in-out 3";
      row.style.setProperty("--highlight-color", "rgba(7, 6, 57, 0.15)");

      // Create keyframes if not exists
      if (!document.getElementById("highlight-keyframes")) {
        const style = document.createElement("style");
        style.id = "highlight-keyframes";
        style.textContent = `
          @keyframes highlight-pulse {
            0%, 100% { background-color: transparent; }
            50% { background-color: rgba(7, 6, 57, 0.15); }
          }
        `;
        document.head.appendChild(style);
      }

      // Remove animation after 3 seconds
      setTimeout(() => {
        row.style.animation = "";
      }, 3000);
    }
  });
}

export default clientHOC(ClientInvitationStatusListExpandedRowData);
